import CurrentUserPhotoEditor from '@/components/CurrentUserPhotoEditor';
import { getRoleLabel } from '@/constants';
import { isValidPhoneNumber } from '@/pages/Beneficiaries/phoneValidation';
import {
  deleteCurrentUserPhoto,
  updateCurrentUserPhoto,
  updateCurrentUserProfile,
  type UpdateCurrentUserProfilePayload,
} from '@/services/user/profileService';
import type { User } from '@/types/user';
import { getApiErrorMessage } from '@/utils/apiError';
import { normalizeImageSource } from '@/utils/imageSource';
import { logger } from '@/utils/logger';
import { LockOutlined } from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  message,
  theme,
} from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

type CurrentUserProfileUpdateFormValues = {
  email: string;
  role: string;
  first_name: string;
  last_name: string;
  document_number: string;
  phone_number?: string;
};

type CurrentUserProfileUpdateFormProps = {
  currentUser?: User;
  submitText?: string;
  cancelText?: string;
  onCancel?: () => void;
  onSuccess?: () => void | Promise<void>;
  showCancelButton?: boolean;
  photoModalWidth?: number | string;
  onOpenPasswordUpdate?: () => void;
};

type NormalizedProfileValues = {
  first_name: string;
  last_name: string;
  document_number: string;
  phone_number: string | null;
};

const normalizeFormValues = (
  values: CurrentUserProfileUpdateFormValues,
): NormalizedProfileValues => ({
  first_name: values.first_name.trim(),
  last_name: values.last_name.trim(),
  document_number: values.document_number.trim(),
  phone_number: values.phone_number?.trim() || null,
});

const normalizeUserProfile = (user?: User): NormalizedProfileValues => ({
  first_name: user?.profile?.first_name?.trim() ?? '',
  last_name: user?.profile?.last_name?.trim() ?? '',
  document_number: user?.profile?.document_number?.trim() ?? '',
  phone_number: user?.profile?.phone_number?.trim() || null,
});

const areProfilesEqual = (
  first: NormalizedProfileValues,
  second: NormalizedProfileValues,
) =>
  first.first_name === second.first_name &&
  first.last_name === second.last_name &&
  first.document_number === second.document_number &&
  first.phone_number === second.phone_number;

export const getProfileValuesAllowedByRole = (
  role: User['role'] | undefined,
  nextValues: NormalizedProfileValues,
  currentValues: NormalizedProfileValues,
): NormalizedProfileValues => {
  if (role !== 'BENEFICIARY') {
    return nextValues;
  }

  return {
    ...currentValues,
    phone_number: nextValues.phone_number,
  };
};

const resolveInitialPhoto = (user?: User) =>
  normalizeImageSource(user?.profile?.photo) ??
  normalizeImageSource(user?.profile?.thumbnail) ??
  null;

const isImageDataUrl = (value?: string | null) =>
  Boolean(value?.startsWith('data:image/'));

const CurrentUserProfileUpdateForm: React.FC<
  CurrentUserProfileUpdateFormProps
> = ({
  currentUser,
  submitText = 'Actualizar',
  cancelText = 'Cancelar',
  onCancel,
  onSuccess,
  showCancelButton,
  photoModalWidth,
  onOpenPasswordUpdate,
}) => {
  const { token } = theme.useToken();
  const [form] = Form.useForm<CurrentUserProfileUpdateFormValues>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoDirty, setPhotoDirty] = useState(false);
  const shouldShowCancelButton = showCancelButton ?? Boolean(onCancel);
  const isBeneficiary = currentUser?.role === 'BENEFICIARY';
  const initialProfileValues = useMemo(
    () => normalizeUserProfile(currentUser),
    [currentUser],
  );
  const initialPhoto = useMemo(
    () => resolveInitialPhoto(currentUser),
    [currentUser],
  );

  useEffect(() => {
    form.setFieldsValue({
      email: currentUser?.email ?? '',
      role: currentUser?.role ?? '',
      first_name: currentUser?.profile?.first_name ?? '',
      last_name: currentUser?.profile?.last_name ?? '',
      document_number: currentUser?.profile?.document_number ?? '',
      phone_number: currentUser?.profile?.phone_number ?? '',
    });
    setPhotoPreview(initialPhoto);
    setPhotoDirty(false);
  }, [currentUser, form, initialPhoto]);

  const roleOptions = useMemo(() => {
    if (!currentUser?.role) {
      return [];
    }
    return [
      {
        label: getRoleLabel(currentUser.role),
        value: currentUser.role,
      },
    ];
  }, [currentUser?.role]);

  const handlePhotoChange = useCallback(
    (nextImageDataUrl: string) => {
      setPhotoPreview(nextImageDataUrl);
      setPhotoDirty(nextImageDataUrl !== initialPhoto);
    },
    [initialPhoto],
  );

  const handlePhotoReset = useCallback(() => {
    setPhotoPreview(initialPhoto);
    setPhotoDirty(false);
  }, [initialPhoto]);

  const handlePhotoDelete = useCallback(() => {
    setPhotoPreview(null);
    setPhotoDirty(initialPhoto !== null);
  }, [initialPhoto]);

  const handleFinish = useCallback(
    async (values: CurrentUserProfileUpdateFormValues) => {
      if (!currentUser) {
        message.error('No encontramos la sesión del usuario actual');
        return;
      }

      const normalizedFormValues = getProfileValuesAllowedByRole(
        currentUser.role,
        normalizeFormValues(values),
        initialProfileValues,
      );
      const hasProfileChanges = !areProfilesEqual(
        normalizedFormValues,
        initialProfileValues,
      );
      const hasPhotoChanges =
        photoDirty &&
        (photoPreview === null ||
          photoPreview === '' ||
          isImageDataUrl(photoPreview));

      if (!hasProfileChanges && !hasPhotoChanges) {
        message.info('No hay cambios para guardar');
        return;
      }

      setIsSubmitting(true);
      try {
        const tasks: Array<{
          key: 'profile' | 'photo';
          run: () => Promise<void>;
        }> = [];

        if (hasProfileChanges) {
          const profilePayload: UpdateCurrentUserProfilePayload = {
            ...normalizedFormValues,
          };
          tasks.push({
            key: 'profile',
            run: () => updateCurrentUserProfile(profilePayload),
          });
        }

        if (hasPhotoChanges) {
          tasks.push({
            key: 'photo',
            run: () =>
              photoPreview && isImageDataUrl(photoPreview)
                ? updateCurrentUserPhoto({ photo: photoPreview })
                : deleteCurrentUserPhoto(),
          });
        }

        const results = await Promise.allSettled(
          tasks.map((task) => task.run()),
        );
        const failures = results
          .map((result, index) => ({
            result,
            key: tasks[index].key,
          }))
          .filter(
            (
              entry,
            ): entry is {
              result: PromiseRejectedResult;
              key: 'profile' | 'photo';
            } => entry.result.status === 'rejected',
          );

        if (failures.length > 0) {
          const successCount = tasks.length - failures.length;
          logger.error('Profile update failed partially', {
            scope: 'current_user_profile_update_form',
            metadata: {
              failures: failures.map((failure) => ({
                key: failure.key,
                reason: failure.result.reason,
              })),
            },
          });

          if (successCount > 0) {
            if (failures.some((failure) => failure.key === 'photo')) {
              message.warning(
                'Se actualizó tu perfil, pero no pudimos guardar la foto.',
              );
            } else {
              message.warning(
                'Se guardaron algunos cambios, pero faltan actualizaciones por aplicar.',
              );
            }
          } else {
            message.error(
              getApiErrorMessage(
                failures[0].result.reason,
                'No pudimos actualizar tu perfil. Por favor intentá nuevamente.',
              ),
            );
          }
          return;
        }

        setPhotoDirty(false);
        message.success('Perfil actualizado correctamente');
        await onSuccess?.();
      } catch (error) {
        logger.error('Update profile error', {
          scope: 'current_user_profile_update_form',
          error,
        });
        message.error(
          getApiErrorMessage(
            error,
            'No pudimos actualizar tu perfil. Por favor intentá nuevamente.',
          ),
        );
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentUser, initialProfileValues, onSuccess, photoDirty, photoPreview],
  );

  const handleCancel = useCallback(() => {
    form.resetFields();
    setPhotoPreview(initialPhoto);
    setPhotoDirty(false);
    onCancel?.();
  }, [form, initialPhoto, onCancel]);

  return (
    <Form form={form} layout="vertical" onFinish={handleFinish}>
      <Row gutter={[20, 16]} align="top">
        <Col xs={24} md={8}>
          <Card
            size="small"
            title="Foto"
            styles={{
              body: {
                height: '100%',
              },
            }}
            style={{
              height: '100%',
              borderColor: token.colorBorder,
              borderStyle: 'dotted',
              borderWidth: 1,
              borderRadius: 8,
            }}
          >
            <CurrentUserPhotoEditor
              imageUrl={photoPreview}
              hasPendingChanges={photoDirty}
              onChange={handlePhotoChange}
              onDelete={handlePhotoDelete}
              onReset={handlePhotoReset}
              disabled={isSubmitting}
              modalWidth={photoModalWidth}
            />
          </Card>
          {onOpenPasswordUpdate && (
            <Button
              block
              icon={<LockOutlined />}
              disabled={isSubmitting}
              style={{ marginTop: 12 }}
              onClick={onOpenPasswordUpdate}
            >
              Actualizar contraseña
            </Button>
          )}
        </Col>
        <Col xs={24} md={16}>
          <Form.Item name="email" label="Email">
            <Input disabled maxLength={255} />
          </Form.Item>
          <Form.Item name="role" label="Rol">
            <Select disabled options={roleOptions} />
          </Form.Item>
          <Form.Item
            name="first_name"
            label="Nombre"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Ingresá el nombre',
              },
              { max: 100, message: 'Máximo 100 caracteres' },
            ]}
          >
            <Input
              placeholder="Nombre"
              maxLength={100}
              disabled={isSubmitting || isBeneficiary}
            />
          </Form.Item>
          <Form.Item
            name="last_name"
            label="Apellido"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Ingresá el apellido',
              },
              { max: 100, message: 'Máximo 100 caracteres' },
            ]}
          >
            <Input
              placeholder="Apellido"
              maxLength={100}
              disabled={isSubmitting || isBeneficiary}
            />
          </Form.Item>
          <Form.Item
            name="document_number"
            label="Número de documento"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Ingresá el número de documento',
              },
              { max: 20, message: 'Máximo 20 caracteres' },
            ]}
          >
            <Input
              placeholder="Documento"
              maxLength={20}
              disabled={isSubmitting || isBeneficiary}
            />
          </Form.Item>
          <Form.Item
            name="phone_number"
            label="Teléfono"
            rules={[
              { max: 20, message: 'Máximo 20 caracteres' },
              {
                validator: (_, value: string | undefined) => {
                  const phoneValue = value?.trim();
                  if (!phoneValue) {
                    return Promise.resolve();
                  }

                  if (!isValidPhoneNumber(phoneValue)) {
                    return Promise.reject(
                      new Error(
                        'Teléfono con formato inválido. Ejemplo: +54 11 0000-0000',
                      ),
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder="Teléfono (opcional)"
              maxLength={20}
              disabled={isSubmitting}
            />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0 }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {shouldShowCancelButton && (
                <Button onClick={handleCancel}>{cancelText}</Button>
              )}
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={!currentUser}
              >
                {submitText}
              </Button>
            </div>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default CurrentUserProfileUpdateForm;
